import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, RefreshCw, Search } from 'lucide-react'
import { Modal } from '../admin/primitives.js'
import { Combobox } from '../admin/pickers.js'
import { Field } from '../admin/form.js'
import { changerStatut, chargerListe, chargerResponsables, type CorpsTransition } from './signalements-api.js'
import {
  FILTRES_DEFAUT, libelle, limite, nomDe, requeteListe, tonDe,
  type ActionStatut, type Changement, type ErreurSig, type Fiche, type MetaSignalements, type Resume, type Responsable,
} from './signalements-model.js'
import { EtatErreur, Pastille, ZoneTexte } from './SignalementsCommun.js'

/**
 * SIGNALEMENTS — CHANGER LE STATUT, UNE MODALE COURTE (09/10/2026).
 *
 * Technique est la source de vérité : la modale ne propose que l'action que
 * la fiche a reçue dans `transitions_possibles`, et ne demande que ce que le
 * catalogue exige pour y entrer (`meta.statuts[cible].requires`, plus
 * `meta.reouverture_requires` pour rouvrir). La demande part avec la VERSION
 * lue : si un collègue a modifié la fiche entre-temps, Technique refuse
 * (`VERSION_CONFLICT`). La modale RESTE alors ouverte, avec ce qui a été
 * saisi (motif, résumé de résolution, original d'un doublon) : une alerte le
 * dit, « Actualiser » relit la fiche et montre ce qui a changé, et c'est
 * l'utilisateur qui valide de nouveau — rien n'est renvoyé automatiquement
 * (contrat BFF §6 : « la saisie est gardée »). Fermer la modale sur un 409
 * faisait perdre, par exemple, un résumé de résolution de 800 caractères.
 */

export function ModaleStatut(props: {
  /** L'action recalculée sur la fiche COURANTE (relue après un conflit). */
  action: ActionStatut
  /** Faux quand la fiche relue n'offre plus cette transition. */
  possible: boolean
  fiche: Fiche
  meta: MetaSignalements
  onFermer: () => void
  onFait: (f: Fiche) => void
  /** Un 409 `VERSION_CONFLICT` : la fiche le note aussi. La modale reste ouverte. */
  onConflit: () => void
  /** Relit la fiche (la nouvelle arrive par `fiche`) ; rend ce qui a changé, ou null si la relecture a échoué. */
  onActualiser: () => Promise<Changement[] | null>
}) {
  const { action, fiche, meta } = props
  const [motif, setMotif] = useState('')
  const [resume, setResume] = useState('')
  const [doublon, setDoublon] = useState<Resume | null>(null)
  const [responsable, setResponsable] = useState<string | null>(null)
  const [responsables, setResponsables] = useState<Responsable[]>([])
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<ErreurSig | null>(null)
  const [champs, setChamps] = useState<Record<string, string>>({})
  const [conflit, setConflit] = useState<null | { etape: 'detecte' } | { etape: 'actualise'; changements: Changement[] }>(null)
  const [actualisation, setActualisation] = useState(false)

  const exigeMotif = action.exige.includes('motif')
  const exigeResume = action.exige.includes('resolution_summary')
  const exigeDoublon = action.exige.includes('duplicate_of_id')

  useEffect(() => {
    if (!action.responsableFacultatif) return
    let vivant = true
    void chargerResponsables().then((r) => { if (vivant && r.ok) setResponsables(r.data) })
    return () => { vivant = false }
  }, [action.responsableFacultatif])

  const manque = !props.possible ? 'Ce changement de statut n’est plus possible depuis le statut actuel.'
    : conflit?.etape === 'detecte' ? 'Actualisez d’abord la fiche.'
      : (exigeMotif && !motif.trim()) ? 'Le motif est obligatoire.'
        : (exigeResume && !resume.trim()) ? 'Le résumé de résolution est obligatoire.'
          : (exigeDoublon && !doublon) ? 'Choisissez le signalement original.'
            : null

  /** « Actualiser » dans la modale : la fiche est relue, la saisie de la modale ne bouge pas. */
  const actualiser = async () => {
    setActualisation(true)
    const changements = await props.onActualiser()
    setActualisation(false)
    if (changements) setConflit({ etape: 'actualise', changements })
  }

  const envoyer = async () => {
    if (manque || envoi) return
    setEnvoi(true)
    setErreur(null)
    setChamps({})
    const corps: CorpsTransition = { version: fiche.version, statut: action.cible }
    if ((exigeMotif || action.motifFacultatif) && motif.trim()) corps.motif = motif.trim()
    if (exigeResume) corps.resolution_summary = resume.trim()
    if (exigeDoublon && doublon) corps.duplicate_of_id = doublon.id
    if (action.responsableFacultatif && responsable) corps.responsable_id = responsable
    const r = await changerStatut(fiche.reference, corps)
    setEnvoi(false)
    if (r.ok) { props.onFait(r.data); return }
    if (r.erreur.genre === 'version') { setConflit({ etape: 'detecte' }); props.onConflit(); return }
    setChamps(r.erreur.champs)
    setErreur(r.erreur)
    // Le compte choisi n'est plus actif : la liste est relue (il n'y est plus proposé) et le choix,
    // s'il en est sorti, est retiré — le champ ne montre pas « Moi » en gardant un compte refusé.
    if (r.erreur.code === 'RESPONSABLE_INCONNU') {
      const refuse = responsables.find((x) => x.id === responsable)?.display_name ?? 'Le compte choisi'
      void chargerResponsables().then((l) => {
        if (!l.ok) return
        setResponsables(l.data)
        if (responsable !== null && !l.data.some((x) => x.id === responsable)) {
          setResponsable(null)
          setChamps({ responsable_id: `${refuse} n’est plus un compte actif : il a été retiré du choix. Choisissez quelqu’un d’autre, ou laissez vide pour en devenir responsable.` })
        }
      })
    }
  }

  // Une erreur qui porte sur des champs AFFICHÉS (RESPONSABLE_INCONNU, VALIDATION_FAILED d'un motif…) s'écrit
  // sous eux ; le bloc du bas ne la répète pas. Un champ que la modale n'affiche pas garde le bloc du bas.
  const affiches: string[] = [
    ...(exigeMotif || action.motifFacultatif ? ['motif'] : []), ...(exigeResume ? ['resolution_summary'] : []),
    ...(exigeDoublon ? ['duplicate_of_id'] : []), ...(action.responsableFacultatif ? ['responsable_id'] : []),
  ]
  const surLesChamps = Object.keys(champs).length > 0 && Object.keys(champs).every((c) => affiches.includes(c))

  const cibleLabel = libelle(meta.statuts, action.cible)
  return (
    <Modal open onClose={props.onFermer} ariaLabel={action.libelle} width={560} owning>
      <div className="lc-modal__head">
        <div>
          <h2 className="lc-modal__title">{action.libelle}</h2>
          <p className="sig-aide">{fiche.reference} · {fiche.titre}</p>
        </div>
      </div>
      <div className="lc-modal__body sig-modale">
        {/* Une action devenue impossible (fiche relue après un 409) n'annonce plus d'effet ni ne garde de
            champs : rien ne peut être envoyé, l'alerte dit pourquoi et où reprendre. */}
        {props.possible ? (
          <>
            <p className="sig-modale__effet">
              {libelle(meta.statuts, fiche.statut)} → <Pastille ton={tonDe(meta.statuts, action.cible)} label={cibleLabel} />
            </p>
            {/* Qui devient responsable se lit sous le champ Responsable (aide), une seule fois et selon le choix fait. */}
            {action.reouverture ? <p className="sig-aide sig-modale__note">La résolution précédente reste dans l’historique.</p> : null}
          </>
        ) : null}

        {props.possible && exigeResume ? (
          <Field label="Résumé de résolution" error={champs['resolution_summary'] ?? null} helper="Ce qui a été fait. Il restera dans l’historique du signalement.">
            <ZoneTexte value={resume} onChange={setResume} rows={4} autoFocus maxLength={limite(meta, 'resolution_summary')}
              ariaLabel="Résumé de résolution" invalide={!!champs['resolution_summary']} />
          </Field>
        ) : null}

        {props.possible && (exigeMotif || action.motifFacultatif) ? (
          <Field label={exigeMotif ? 'Motif' : 'Motif (facultatif)'} error={champs['motif'] ?? null}
            helper={action.reouverture ? 'Pourquoi le signalement est rouvert.' : action.cible === 'en_attente' ? 'Ce qu’on attend : une pièce, une intervention, une réponse…' : undefined}>
            <ZoneTexte value={motif} onChange={setMotif} rows={3} autoFocus={!exigeResume} maxLength={limite(meta, 'motif')}
              ariaLabel="Motif" invalide={!!champs['motif']} />
          </Field>
        ) : null}

        {props.possible && exigeDoublon ? (
          <Field label="Signalement original" error={champs['duplicate_of_id'] ?? null}
            helper="Le signalement qui reste ouvert ; celui-ci sera fermé comme doublon.">
            <RechercheDoublon fiche={fiche} meta={meta} choisi={doublon} onChoisir={setDoublon} />
          </Field>
        ) : null}

        {props.possible && action.responsableFacultatif ? (
          <Field label="Responsable" error={champs['responsable_id'] ?? null}
            helper={fiche.responsable ? `Actuellement : ${nomDe(fiche.responsable)}.` : responsable ? undefined : 'Laisser vide : vous devenez responsable.'}>
            <Combobox value={responsable} clearable placeholder={fiche.responsable ? 'Garder le responsable actuel' : 'Moi'}
              ariaLabel="Responsable" onChange={setResponsable}
              items={responsables.map((r) => ({ value: r.id, label: r.display_name, ...(r.can_handle ? {} : { hint: 'sans droit de traitement' }) }))} />
          </Field>
        ) : null}

        {conflit ? (
          <div className="sig-conflit" role="alert">
            <AlertTriangle size={18} aria-hidden="true" className="sig-conflit__icone" />
            <div className="sig-conflit__texte">
              <p className="sig-conflit__titre">Ce signalement a été modifié pendant que vous remplissiez ce formulaire.</p>
              {conflit.etape === 'detecte' ? (
                <p>Rien n’a été enregistré. Actualisez la fiche : ce que vous avez saisi ici est conservé.</p>
              ) : (
                <>
                  <p>
                    {conflit.changements.length > 0 ? 'Voici ce qui a changé. ' : 'La fiche est à jour. '}
                    {props.possible ? 'Votre saisie est conservée : relisez-la, puis validez de nouveau.'
                      : `Ce changement de statut n’est plus possible depuis\u00a0«\u00a0${libelle(meta.statuts, fiche.statut)}\u00a0». Fermez cette fenêtre : les actions encore possibles sont en haut de la fiche.`}
                  </p>
                  {conflit.changements.length > 0 ? (
                    <ul className="sig-conflit__liste">
                      {conflit.changements.map((c) => <li key={c.champ}><strong>{c.libelle}</strong> : {c.avant} → {c.apres}</li>)}
                    </ul>
                  ) : null}
                </>
              )}
            </div>
            {conflit.etape === 'detecte' ? (
              <button type="button" className="lc-btn lc-btn--sm" onClick={() => void actualiser()} disabled={actualisation}>
                <RefreshCw size={14} aria-hidden="true" /> {actualisation ? 'Lecture…' : 'Actualiser'}
              </button>
            ) : null}
          </div>
        ) : null}

        {erreur && !surLesChamps ? <EtatErreur erreur={erreur} compact /> : null}

        <div className="sig-modale__pied">
          {props.possible ? (
            <>
              <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onFermer}>Annuler</button>
              <button type="button" className={`lc-btn ${action.cible === 'rejete' ? 'lc-btn--danger' : 'lc-btn--primary'}`}
                disabled={!!manque || envoi} title={manque ?? undefined} onClick={() => void envoyer()}>
                {envoi ? 'Envoi…' : action.libelle}
              </button>
            </>
          ) : (
            <button type="button" className="lc-btn lc-btn--primary" onClick={props.onFermer}>Fermer</button>
          )}
        </div>
      </div>
    </Modal>
  )
}

/**
 * Désigner l'ORIGINAL d'un doublon : une recherche dans la liste de Technique
 * (`?q=`, tous états), jamais la fiche elle-même. On envoie son identifiant
 * NUMÉRIQUE ; un signalement déjà doublon est montré, mais pas choisissable.
 */
function RechercheDoublon(props: { fiche: Fiche; meta: MetaSignalements; choisi: Resume | null; onChoisir: (r: Resume | null) => void }) {
  const [q, setQ] = useState('')
  const [resultats, setResultats] = useState<Resume[]>([])
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<ErreurSig | null>(null)
  const generation = useRef(0)

  useEffect(() => {
    const terme = q.trim()
    generation.current += 1
    const g = generation.current
    if (terme.length < 2) { setResultats([]); setErreur(null); setEnCours(false); return }
    setEnCours(true)
    const t = window.setTimeout(() => {
      void chargerListe(requeteListe({ ...FILTRES_DEFAUT, q: terme.slice(0, limite(props.meta, 'recherche')), state: 'all', sort: 'recent' }, 10)).then((r) => {
        if (g !== generation.current) return
        setEnCours(false)
        if (r.ok) { setResultats(r.data.items.filter((x) => x.id !== props.fiche.id)); setErreur(null) } else setErreur(r.erreur)
      })
    }, 300)
    return () => window.clearTimeout(t)
  }, [q, props.fiche.id, props.meta])

  if (props.choisi) {
    const c = props.choisi
    return (
      <div className="sig-doublon__choisi">
        <span className="sig-ref">{c.reference}</span>
        <span className="sig-doublon__titre">{c.titre}</span>
        <Pastille ton={tonDe(props.meta.statuts, c.statut)} label={libelle(props.meta.statuts, c.statut)} />
        <button type="button" className="lc-btn lc-btn--sm lc-btn--ghost" onClick={() => props.onChoisir(null)}>Changer</button>
      </div>
    )
  }
  return (
    <div className="sig-doublon">
      <label className="lc-search sig-materiel__recherche">
        <Search size={16} className="lc-search__icon" aria-hidden="true" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
          placeholder="Référence ou mots du titre" aria-label="Rechercher le signalement original" />
      </label>
      <p className="sig-aide" aria-live="polite">
        {q.trim().length < 2 ? 'Tapez au moins deux caractères.' : enCours ? 'Recherche…'
          : !erreur && resultats.length === 0 ? 'Aucun autre signalement ne correspond.' : null}
      </p>
      {erreur ? <EtatErreur erreur={erreur} compact /> : null}
      {resultats.length > 0 ? (
        <ul className="sig-materiel__liste">
          {resultats.map((r) => {
            const estDoublon = r.statut === 'doublon'
            return (
              <li key={r.id}>
                <button type="button" className="sig-materiel__choix" disabled={estDoublon} onClick={() => props.onChoisir(r)}>
                  <span className="sig-materiel__ligne">
                    <span className="sig-materiel__nom"><span className="sig-ref">{r.reference}</span><strong>{r.titre}</strong></span>
                    <span className="sig-materiel__meta">
                      <Pastille ton={tonDe(props.meta.statuts, r.statut)} label={libelle(props.meta.statuts, r.statut)} />
                      {estDoublon ? 'déjà doublon : choisissez son original' : null}
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
